/**
 * Countdown gate for "resend email" affordances.
 *
 * `start()` arms the cooldown; `secondsLeft` ticks down to 0 (ready).
 * Timestamp-based (not decrement-based) so it stays accurate when the JS
 * thread stalls or the interval drifts.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** Tick faster than 1s so the displayed second never visibly skips. */
const COOLDOWN_TICK_MS = 250;

export function useResendCooldown(cooldownMs: number) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const endsAtRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTicking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    const endsAt = endsAtRef.current;
    const remaining = endsAt ? Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)) : 0;
    setSecondsLeft(remaining);
    if (remaining <= 0) stopTicking();
  }, [stopTicking]);

  /** Arm (or re-arm) the cooldown — call after every send. */
  const start = useCallback(() => {
    endsAtRef.current = Date.now() + cooldownMs;
    stopTicking();
    intervalRef.current = setInterval(tick, COOLDOWN_TICK_MS);
    tick();
  }, [cooldownMs, stopTicking, tick]);

  /** Disarm and go back to ready (e.g. when leaving the success state). */
  const reset = useCallback(() => {
    endsAtRef.current = null;
    stopTicking();
    setSecondsLeft(0);
  }, [stopTicking]);

  useEffect(() => stopTicking, [stopTicking]);

  return { secondsLeft, isCoolingDown: secondsLeft > 0, start, reset };
}
